import { inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { Meeting } from '@app/core/model/entities/meeting/meeting';
import {
  MeetingCreateModalComponent
} from '@app/features/main/views/meetings/meetings-inventory/modals/meeting-create-modal/meeting-create-modal.component';
import { MeetingsService } from '@app/features/main/views/meetings/meetings.services';
import { SpinnerService } from '@app/shared/popup/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { EMPTY, finalize, Observable, switchMap } from 'rxjs';

@Injectable()
export class MeetingModalService {

  private readonly popupManager = inject(PopupManager);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly meetingsService = inject(MeetingsService);
  private readonly translate = inject(TranslateService);

  /**
   * Display a dialog for the user to fill data then create a new Meeting with the data.
   * @return Created Meeting.
   */
  public openCreateMeetingDialog(): Observable<Meeting> {
    const dialogRef = this.popupManager.showGenericPopup(
      MeetingCreateModalComponent,
      PopupSize.MEDIUM,
      {}
    );

    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const event = new AnalyticsEvent(ActionEnum.CREATE, EntityTypeEnum.MEETING);

          if (dialogResponse === 'yes') {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE});
            this.analyticsService.trackEvent(event);
            this.spinnerService.showSpinner();
            return this.meetingsService.createMeeting(dialogRef.componentInstance.getGeneratedObject())
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(event);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Display a dialog asking the User to confirm Meetings' deletion, then delete the Meetings if confirmed.
   * @param meetings Meetings to delete.
   * @return True if the Meetings were successfully deleted, false otherwise.
   * @throws Any encountered errors.
   */
  public openDeleteMeetingsDialog(meetings: Meeting[]): Observable<boolean> {
    const dialogRef = this.popupManager.showOkCancelPopup({
      dialogTitle: this.translate.instant(meetings.length === 1 ? 'TITLE.DELETE_MEETING' : 'TITLE.DELETE_MEETINGS'),
      dialogMessage: this.translate.instant(meetings.length === 1 ? 'LABEL.DELETE_MEETING' : 'LABEL.DELETE_MEETINGS'),
      type: 'warning',
      okText: this.translate.instant('LABEL.DELETE')
    });

    return dialogRef.afterClosed().pipe(
      switchMap(dialogResponse => {
        const event = new AnalyticsEvent(ActionEnum.DELETE, EntityTypeEnum.MEETING);

        if (dialogResponse === 'yes') {
          event.addProperties({
            [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.DELETE,
            [AnalyticsKeyEnum.ENTITY_ID]: meetings.map(meeting => meeting.id).toString()
          });
          this.analyticsService.trackEvent(event);
          this.spinnerService.showSpinner();
          return this.meetingsService.deleteMeetings(meetings)
            .pipe(finalize(() => this.spinnerService.hideSpinner()));
        } else {
          event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
          this.analyticsService.trackEvent(event);
          return EMPTY;
        }
      })
    );
  }
}
